import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const Overview = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">How Patient Transport works</Typography>
      <Typography>
        Patient Transport Service (PTS) joins together patient records,
        requesting organisations, booking creation, route planning, crew work,
        dispatch oversight, and the hospital booking portal. A booking is the
        administrative record. Each transport movement inside that booking is a
        journey, and journeys can then be assigned to duties and routes for the
        operational day.
      </Typography>
      <Alert severity="info" variant="outlined">
        Patient Transport is an enterprise module. Co-ordination features also
        need the PTS Co-Ordinator permission, selected base locations, and
        portal access where relevant — crew screens are open to every employee.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Core records</Typography>
      <Typography>
        The module is built around a small set of linked records:
      </Typography>
      <ul style={{ marginTop: 0, lineHeight: 2 }}>
        <li>
          <strong>Patients</strong> hold demographics, identifiers, addresses,
          mobility needs, clinical risk notes, and journey notification details.
        </li>
        <li>
          <strong>Organisations</strong> represent hospitals or other requesting
          bodies. They can contain wards, departments, contacts, and hospital
          booking portal users.
        </li>
        <li>
          <strong>Bookings</strong> capture the customer/requester, patient,
          ward or department, pickup and dropoff details, recurrence, return
          journey requirements, and source of request.
        </li>
        <li>
          <strong>Journeys</strong> are the individual transport legs that need
          planning, assigning, dispatching, and completion.
        </li>
        <li>
          <strong>Duties and routes</strong> convert planned journeys into live
          work for vehicles, crew, and dispatchers.
        </li>
      </ul>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Typical workflow</Typography>
      <Typography>
        A coordinator or hospital portal user creates a booking after selecting
        or creating the patient. AmbuNet stores requester and ward information,
        creates the required journeys, and leaves those journeys available for
        planning unless they are assigned during booking creation.
      </Typography>
      <Typography>
        Planners build duties for the day, add vehicles and crew shifts, and
        place unassigned journeys into routes. Dispatchers then use the control
        screen to monitor active duties, handle late allocation changes, follow
        journey status, and communicate with crew or hospital portal users.
      </Typography>
      <Typography>
        A duty can also be <strong>dedicated</strong> to one hospital or
        organisation — a discharge crew the hospital books straight onto
        through its portal, optionally with no dispatch at all (the crew add
        their own jobs) and billed as a whole duty rather than per journey.
        See{' '}
        <DocLink
          url="/patient-transport/planning"
          text="Planning"
          docsPage
        />
        .
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Main entry points</Typography>
      <Typography>
        Use <DocLink url="/pts/patients" text="PTS Patients" />
        for patient records, <DocLink url="/pts/bookings" text="PTS Bookings" />
        for booking creation and review,{' '}
        <DocLink url="/pts/planning" text="PTS Planning" />
        for duties and routes,{' '}
        <DocLink url="/pts/control" text="PTS Control" />
        for dispatch,{' '}
        <DocLink url="/pts/organisations" text="PTS Organisations" />
        for hospitals, wards, departments, and hospital portal users, and{' '}
        <DocLink url="/pts" text="PTS" /> (crew view) for whoever is actually
        driving a duty.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Access model</Typography>
      <Typography>
        Internal staff have a single PTS permission, set from{' '}
        <DocLink
          url="/site-configuration/pts-access"
          text="Configuration > Patient Transport > PTS Access"
        />
        : <strong>Co-Ordinator</strong> unlocks the whole module — bookings,
        planning, dispatch, patients, organisations, reports, and invoicing.
        There is no separate "Crew" permission: the My PTS Duty screen (and
        the mobile app equivalent) for working assigned journeys is open to
        every employee on the Enterprise tier, so anyone can be rostered onto
        a duty. Hospital portal users are linked to a PTS organisation and
        have a portal access level of Ward, Site, or Admin.
      </Typography>
      <Typography>
        Ward users only see work for their permitted wards. Site and Admin users
        can work across the organisation, with wider reporting and management
        views depending on the portal screen. If a Ward access user has no wards
        assigned, AmbuNet blocks the portal data views and shows a clear access
        message while still allowing Help and Support.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Quoting, invoicing, and performance</Typography>
      <Typography>
        Beyond planning and dispatch, PTS also prices and bills the work it
        carries out. Tariffs turn journeys into quotes and invoices, contracts
        record how each organisation is billed, billing runs consolidate a
        period of work into draft invoices, and reporting shows on-time
        performance and portal adoption. See{' '}
        <DocLink
          url="/patient-transport/invoicing"
          text="Quoting, Invoicing & Billing"
          docsPage
        />{' '}
        and{' '}
        <DocLink
          url="/patient-transport/reports"
          text="Reports & Performance"
          docsPage
        />.
      </Typography>
      <Typography>
        Patients and wards can also be sent a link to a simple, no-login
        tracking page once their crew is on the way — see{' '}
        <DocLink
          url="/patient-transport/live-tracking"
          text="Live Journey Tracking"
          docsPage
        />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Where to go next</Typography>
      <Typography>
        Read the booking article for the booking form, the planning article for
        routes and duties, the control article for dispatch, the crew view
        article for what crew members see day-to-day, the{' '}
        <DocLink url="/patient-transport/mobile-app" text="Mobile App" docsPage />{' '}
        article for the native phone equivalent of crew view, the hospital
        portal article for external requester access, and the invoicing and
        reports articles for the commercial and performance side of the
        module.
      </Typography>
    </Stack>
  </Stack>
)

export default Overview
